import type { ReactNode } from 'react'
import { cn } from '../../lib/utils'

export type Variant = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'muted' | 'brand'

const variants: Record<Variant, string> = {
  default: 'bg-zinc-800/70 text-zinc-200 border border-zinc-700/60',
  brand:
    'bg-cyan-500/12 text-cyan-300 border border-cyan-500/40 shadow-[0_0_12px_-2px_rgba(34,211,238,0.4),0_0_0_1px_rgba(34,211,238,0.08)_inset]',
  success: 'bg-emerald-500/12 text-emerald-300 border border-emerald-500/30',
  warning: 'bg-amber-500/12 text-amber-300 border border-amber-500/30',
  danger: 'bg-pink-500/12 text-pink-300 border border-pink-500/30',
  info: 'bg-violet-500/12 text-violet-300 border border-violet-500/30',
  muted: 'bg-zinc-800/40 text-zinc-400 border border-zinc-700/40',
}

export function Badge({
  variant = 'default',
  children,
  className,
}: {
  variant?: Variant
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[11px] font-medium tracking-[0.005em] leading-none whitespace-nowrap',
        variants[variant],
        className,
      )}
    >
      {children}
    </span>
  )
}

// Status mapping helpers
const clientStatusMap: Record<string, Variant> = {
  lead: 'info',
  active: 'success',
  inactive: 'muted',
  archived: 'muted',
}
export const clientStatusVariant = (s?: string | null): Variant =>
  s ? (clientStatusMap[s] ?? 'default') : 'default'

const projectStatusMap: Record<string, Variant> = {
  lead: 'info',
  proposal: 'info',
  active: 'success',
  on_hold: 'warning',
  completed: 'muted',
  cancelled: 'danger',
}
export const projectStatusVariant = (s?: string | null): Variant =>
  s ? (projectStatusMap[s] ?? 'default') : 'default'

const taskStatusMap: Record<string, Variant> = {
  todo: 'muted',
  in_progress: 'brand',
  in_review: 'warning',
  blocked: 'danger',
  done: 'success',
  cancelled: 'muted',
  draft: 'muted',
  ready: 'info',
  planned: 'info',
  completed: 'success',
}
export const taskStatusVariant = (s?: string | null): Variant =>
  s ? (taskStatusMap[s] ?? 'default') : 'default'

const leadStatusMap: Record<string, Variant> = {
  new: 'info',
  contacted: 'info',
  qualified: 'info',
  proposal: 'warning',
  negotiation: 'warning',
  won: 'success',
  lost: 'danger',
  archived: 'muted',
}
export const leadStatusVariant = (s?: string | null): Variant =>
  s ? (leadStatusMap[s] ?? 'default') : 'default'

const proposalStatusMap: Record<string, Variant> = {
  draft: 'muted',
  sent: 'info',
  pending_review: 'warning',
  accepted: 'success',
  rejected: 'danger',
  expired: 'warning',
  superseded: 'muted',
}
export const proposalStatusVariant = (s?: string | null): Variant =>
  s ? (proposalStatusMap[s] ?? 'default') : 'default'

const proposalStatusLabel: Record<string, string> = {
  draft: 'Bozza',
  sent: 'Inviata',
  pending_review: 'In Attesa',
  accepted: 'Accettata',
  rejected: 'Rifiutata',
  expired: 'Scaduta',
  superseded: 'Sostituita',
}
export const labelForProposalStatus = (s?: string | null): string =>
  s ? (proposalStatusLabel[s] ?? s) : ''
